import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import authService from "@/services/authService";
import pixService from "@/services/pixService";
import { printOrder } from "@/services/printService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MessageCircleCode,
  Bell,
  CircleDollarSign,
  PackageCheck,
  Check,
  Angry,
  Eye,
  AlertCircle,
  ShoppingBag,
  Clock,
  DollarSign,
  Search,
  X,
  Printer,
  Phone,
  MapPin,
  ChevronDown,
  ChevronUp,
  Calendar,
  Filter,
  Package,
  User,
  Wallet,
  CheckCircle,
  Truck,
  Ticket,
  Copy,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";
const API = `${BACKEND_URL}`;
const PAGE_SIZE = 20;

// Helper functions
const normalizeAddress = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return { description: value };
    }
  }
  return null;
};

const attachNormalizedAddresses = (order) => {
  if (!order || typeof order !== "object") return order;

  const normalizedOrder = { ...order };
  const normalizedAddress = normalizeAddress(order.address);
  const normalizedDeliveryAddress = normalizeAddress(order.delivery_address);
  const normalizedClient = order.client ? { ...order.client } : null;

  if (normalizedAddress) {
    normalizedOrder.address = normalizedAddress;
  }

  if (normalizedDeliveryAddress) {
    normalizedOrder.delivery_address = normalizedDeliveryAddress;
  }

  if (normalizedClient) {
    normalizedClient.address =
      normalizeAddress(order.client.address) ||
      normalizedDeliveryAddress ||
      normalizedAddress;
    normalizedOrder.client = normalizedClient;
  }

  return normalizedOrder;
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const isoCandidate = new Date(trimmed);
    if (!Number.isNaN(isoCandidate.getTime())) return isoCandidate;

    const [datePart, timePart] = trimmed.split(/\s+/);
    if (!datePart) return null;

    const dateSegments = datePart
      .split(/[/-]/)
      .map((segment) => segment.trim());
    if (dateSegments.length !== 3) return null;

    let day, month, year;
    if (dateSegments[0].length === 4) {
      year = Number.parseInt(dateSegments[0], 10);
      month = Number.parseInt(dateSegments[1], 10);
      day = Number.parseInt(dateSegments[2], 10);
    } else if (dateSegments[2].length === 4) {
      day = Number.parseInt(dateSegments[0], 10);
      month = Number.parseInt(dateSegments[1], 10);
      year = Number.parseInt(dateSegments[2], 10);
    } else {
      return null;
    }

    if ([day, month, year].some((part) => Number.isNaN(part))) return null;

    let hours = 0,
      minutes = 0,
      seconds = 0;
    if (timePart) {
      const timeSegments = timePart.split(":").map((segment) => segment.trim());
      hours = Number.parseInt(timeSegments[0] || "0", 10);
      minutes = Number.parseInt(timeSegments[1] || "0", 10);
      seconds = Number.parseInt(timeSegments[2] || "0", 10);

      if (Number.isNaN(hours)) hours = 0;
      if (Number.isNaN(minutes)) minutes = 0;
      if (Number.isNaN(seconds)) seconds = 0;
    }

    return new Date(year, month - 1, day, hours, minutes, seconds);
  }
  return null;
};

const parseOrderDate = (order) => {
  if (!order || typeof order !== "object") return null;

  const potentialValues = [
    order.date_time,
    order.dateTime,
    order.created_at,
    order.createdAt,
    order.created,
    order.createdDate,
    order.date,
    order.updated_at,
    order.updatedAt,
  ];

  for (const value of potentialValues) {
    const parsed = parseDateValue(value);
    if (parsed) return parsed;
  }
  return null;
};

// Modal de Detalhes do Pedido (Responsivo)
const OrderDetailsModal = ({ open, onOpenChange, order, onPrintOrder }) => {
  const [showTextModal, setShowTextModal] = useState(false);
  const [receiptText, setReceiptText] = useState("");

  if (!order) return null;

  const calculateOrderTotal = () => {
    let productsTotal = 0;
    if (order.products && Array.isArray(order.products)) {
      order.products.forEach((product) => {
        productsTotal += parseFloat(product.total_value || 0);
      });
    }
    // Calcular desconto do cupom (aplicado apenas nos produtos)
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    const discountAmount = productsTotal * (discountPercentage / 100);
    const subtotalWithDiscount = productsTotal - discountAmount;

    // Adicionar taxa de entrega se existir
    const freightValue = parseFloat(order.freight_value || 0);
    return subtotalWithDiscount + freightValue;
  };

  const getProductsSubtotal = () => {
    let total = 0;
    if (order.products && Array.isArray(order.products)) {
      order.products.forEach((product) => {
        total += parseFloat(product.total_value || 0);
      });
    }
    return total;
  };

  const getDiscountAmount = () => {
    const productsTotal = getProductsSubtotal();
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    return productsTotal * (discountPercentage / 100);
  };

  const generateReceiptText = () => {
    const lineWidth = 32;
    const separator = "=".repeat(lineWidth);
    const dashedLine = "-".repeat(lineWidth);
    const centerText = (text) => {
      const padding = Math.max(0, Math.floor((lineWidth - text.length) / 2));
      return " ".repeat(padding) + text;
    };

    const removeAccents = (text) => {
      const map = {
        á: "a",
        à: "a",
        ã: "a",
        â: "a",
        ä: "a",
        é: "e",
        è: "e",
        ê: "e",
        ë: "e",
        í: "i",
        ì: "i",
        î: "i",
        ï: "i",
        ó: "o",
        ò: "o",
        õ: "o",
        ô: "o",
        ö: "o",
        ú: "u",
        ù: "u",
        û: "u",
        ü: "u",
        ç: "c",
        ñ: "n",
        Á: "A",
        À: "A",
        Ã: "A",
        Â: "A",
        Ä: "A",
        É: "E",
        È: "E",
        Ê: "E",
        Ë: "E",
        Í: "I",
        Ì: "I",
        Î: "I",
        Ï: "I",
        Ó: "O",
        Ò: "O",
        Õ: "O",
        Ô: "O",
        Ö: "O",
        Ú: "U",
        Ù: "U",
        Û: "U",
        Ü: "U",
        Ç: "C",
        Ñ: "N",
      };
      return text
        .split("")
        .map((char) => map[char] || char)
        .join("");
    };

    const formatDate = (dateString) => {
      if (!dateString) return new Date().toLocaleString("pt-BR");
      return new Date(dateString).toLocaleString("pt-BR");
    };

    const productsSubtotal = getProductsSubtotal();
    const discountAmount = getDiscountAmount();
    const total = calculateOrderTotal();

    let text = "";
    text += centerText("BONFIM GOURMET") + "\n";
    text += centerText("Comanda de Pedido") + "\n";
    text += separator + "\n";
    text += `Pedido #${order.code}\n`;
    text += `Data ${formatDate(order.created_at)}\n`;
    text += `Status ${removeAccents(order.status || "N/A")}\n`;
    text += separator + "\n";

    text += "\nCLIENTE\n";
    const clientName = removeAccents(order.client?.name || "Anonimo");
    if (clientName.length > 32) {
      const words = clientName.split(" ");
      let line = "";
      words.forEach((word) => {
        if ((line + word).length > 32) {
          text += line.trim() + "\n";
          line = word + " ";
        } else {
          line += word + " ";
        }
      });
      text += line.trim() + "\n";
    } else {
      text += clientName + "\n";
    }
    text += `Tel ${order.client?.contact || "N/A"}\n`;
    text += separator + "\n";

    text += "\nITENS DO PEDIDO\n";
    text += dashedLine + "\n";

    if (!order.products || order.products.length === 0) {
      text += "\nNenhum produto encontrado\n";
    }

    order.products?.forEach((product, idx) => {
      const prodName = removeAccents(product.product?.name || "Produto");
      text += `\n${idx + 1}. ${prodName} | Qtd ${product.quantity}\n`;
      text += `   Un. R$ ${(
        parseFloat(product.total_value) / product.quantity
      ).toFixed(2)}\n`;
      text += `   Tot R$ ${parseFloat(product.total_value).toFixed(2)}\n`;

      if (product.selected_produtos && product.selected_produtos.length > 0) {
        text += "\n   Produtos Selecionados:\n";
        product.selected_produtos.forEach((prod) => {
          const pName = removeAccents(prod.name);
          text += `   + ${pName} x${prod.qty}\n`;
          text += `     R$ ${(prod.price * prod.qty).toFixed(2)}\n`;
        });
      }

      if (
        product.selectedExtraProdutos &&
        product.selectedExtraProdutos.length > 0
      ) {
        text += "\n   Produtos Extras:\n";
        product.selectedExtraProdutos.forEach((prod) => {
          const pName = removeAccents(prod.name);
          text += `   + ${pName} x${prod.qty}\n`;
          text += `     R$ ${(prod.price * prod.qty).toFixed(2)}\n`;
        });
      }

      if (
        product.selected_ingredients &&
        product.selected_ingredients.length > 0
      ) {
        text += "\n   Ingredientes:\n";
        product.selected_ingredients.forEach((ing) => {
          const ingName = removeAccents(ing.name);
          text += `   * ${ingName}\n`;
        });
      }

      if (product.observation) {
        text += "\n   Obs:\n";
        const obs = removeAccents(product.observation);
        const obsWords = obs.split(" ");
        let obsLine = "   ";
        obsWords.forEach((word) => {
          if ((obsLine + word).length > 32) {
            text += obsLine.trim() + "\n";
            obsLine = "   " + word + " ";
          } else {
            obsLine += word + " ";
          }
        });
        text += obsLine.trim() + "\n";
      }

      text += dashedLine + "\n";
    });

    text += "\n" + separator + "\n";
    text += `Subtotal R$ ${productsSubtotal.toFixed(2)}\n`;

    if (order.coupon_code) {
      text += `Desconto ${order.coupon_code}\n`;
      text += `${parseFloat(order.discount_percentage || 0).toFixed(
        1
      )}% -R$ ${discountAmount.toFixed(2)}\n`;
    }

    text += separator + "\n";
    text += `TOTAL R$ ${total.toFixed(2)}\n`;
    text += separator + "\n";

    text += `\nPagamento ${removeAccents(order.pay_method || "N/A")}\n`;
    text += `Entrega ${removeAccents(order.delivery_type || "N/A")}\n`;
    if (order.payed) {
      text += "Status PAGO\n";
    }
    text += separator + "\n";
    text += "\n" + centerText("Obrigado!") + "\n";
    text += centerText("BONFIM GOURMET") + "\n\n";

    return text;
  };

  const handleCopyText = () => {
    console.log("handleCopyText chamado");
    const text = generateReceiptText();
    console.log("Texto recebido no handleCopyText, tamanho:", text.length);
    setReceiptText(text);
    setShowTextModal(true);
  };

  const copyToClipboard = async () => {
    console.log("copyToClipboard chamado");
    console.log("receiptText tamanho:", receiptText.length);
    console.log("receiptText conteúdo:", receiptText);

    try {
      // Função para remover acentos e caracteres especiais
      const removeAccents = (text) => {
        const map = {
          á: "a",
          à: "a",
          ã: "a",
          â: "a",
          ä: "a",
          é: "e",
          è: "e",
          ê: "e",
          ë: "e",
          í: "i",
          ì: "i",
          î: "i",
          ï: "i",
          ó: "o",
          ò: "o",
          õ: "o",
          ô: "o",
          ö: "o",
          ú: "u",
          ù: "u",
          û: "u",
          ü: "u",
          ç: "c",
          ñ: "n",
          Á: "A",
          À: "A",
          Ã: "A",
          Â: "A",
          Ä: "A",
          É: "E",
          È: "E",
          Ê: "E",
          Ë: "E",
          Í: "I",
          Ì: "I",
          Î: "I",
          Ï: "I",
          Ó: "O",
          Ò: "O",
          Õ: "O",
          Ô: "O",
          Ö: "O",
          Ú: "U",
          Ù: "U",
          Û: "U",
          Ü: "U",
          Ç: "C",
          Ñ: "N",
        };

        return text
          .split("")
          .map((char) => map[char] || char)
          .join("");
      };

      // Remover acentos do texto
      const textWithoutAccents = removeAccents(receiptText);

      console.log("Texto sem acentos preparado para cópia");

      // Tentar usar a API moderna do clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textWithoutAccents);
        console.log("Copiado com sucesso usando navigator.clipboard");
        alert(
          "✅ Texto copiado para a área de transferência (sem acentos para compatibilidade com impressora térmica)!"
        );
      } else {
        // Fallback para navegadores mais antigos ou contexto não seguro
        const textArea = document.createElement("textarea");
        textArea.value = textWithoutAccents;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand("copy");
        textArea.remove();

        console.log("Resultado do execCommand:", successful);

        if (successful) {
          alert(
            "✅ Texto copiado para a área de transferência (sem acentos para compatibilidade com impressora térmica)!"
          );
        } else {
          throw new Error("execCommand falhou");
        }
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
      console.error("Stack trace:", err.stack);
      alert(
        "⚠️ Não foi possível copiar automaticamente.\n\nPor favor, selecione todo o texto acima e pressione Ctrl+C (ou Cmd+C no Mac) para copiar manualmente."
      );
    }
  };

  const hasFreight =
    order.delivery_type?.toLowerCase() === "delivery" &&
    parseFloat(order.freight_value || 0) > 0;

  const isDelivery = order.delivery_type?.toLowerCase() === "delivery";
  const addressObjects = [
    order.address,
    order.delivery_address,
    order.client,
    order.client?.address,
    order.client?.delivery_address,
  ].filter((src) => src && typeof src === "object");

  const pickAddressValue = (keys) => {
    for (const key of keys) {
      for (const src of addressObjects) {
        const value = src?.[key];
        if (typeof value === "string" && value.trim()) {
          return value.trim();
        }
      }
    }
    return "";
  };

  const street = pickAddressValue([
    "logradouro",
    "street",
    "rua",
    "address",
    "description",
    "address_line",
    "line1",
    "road",
  ]);
  const number = pickAddressValue(["numero", "number", "num"]);
  const complement = pickAddressValue([
    "complemento",
    "complement",
    "address_complement",
  ]);
  const neighborhood = pickAddressValue(["bairro", "neighborhood", "district"]);
  const city = pickAddressValue(["cidade", "city", "municipio", "locality"]);
  const state = pickAddressValue(["estado", "state", "uf"]);
  const cep = pickAddressValue(["cep", "zip", "zipcode", "postal_code"]);
  const reference = pickAddressValue([
    "referencia",
    "reference",
    "ponto_referencia",
  ]);

  const lineOne = street
    ? `${street}${number ? `, Nº ${number}` : ""}${
        complement ? ` (${complement})` : ""
      }`
    : "";

  const structuredSegments = [
    lineOne,
    neighborhood && `Bairro ${neighborhood}`,
    city || state ? [city, state].filter(Boolean).join(" - ") : "",
    cep && `CEP: ${cep}`,
    reference && `Ref.: ${reference}`,
  ].filter((segment) => segment && segment.trim());

  const deliveryAddressText =
    structuredSegments.join(" • ") || "Endereço não informado";

  const mapSeed = [
    street && `${street} ${number || ""}`.trim(),
    neighborhood,
    city,
    state,
    cep,
  ]
    .filter(Boolean)
    .join(", ");

  const mapQuery = mapSeed ? encodeURIComponent(mapSeed) : "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg md:text-xl">
              <ShoppingBag className="h-5 w-5 md:h-6 md:w-6 text-[#c9332C]" />
              Pedido #{order.code}
              {order.payed === true && (
                <Badge className="text-xs md:text-sm bg-green-600 text-white border-0 shadow-sm ml-2">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Pago
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs md:text-sm mt-2 px-6">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <strong>{order.client?.name || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              <strong>{order.client?.contact || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              <strong>{order.pay_method || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              <strong>{order.delivery_type || "N/A"}</strong>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="space-y-4">
            {/* Endereço de Entrega */}
            {isDelivery && (
              <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-indigo-600" />
                  <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">
                    Endereço para entrega
                  </p>
                </div>
                <p className="text-sm text-gray-900 leading-relaxed">
                  {deliveryAddressText}
                </p>

                {mapQuery && (
                  <div className="rounded-lg border border-indigo-200 overflow-hidden h-48 md:h-64">
                    <iframe
                      title={`Mapa entrega ${order.code}`}
                      src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}

            {/* Produtos do Pedido */}
            {order.products && order.products.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-bold text-base md:text-lg flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Itens do Pedido
                </h3>
                {order.products.map((product, productIdx) => (
                  <div
                    key={productIdx}
                    className="border rounded-lg p-3 md:p-4 bg-gray-50 space-y-3"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            #{productIdx + 1}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Qtd: {product.quantity}
                          </Badge>
                        </div>
                        {product.product && (
                          <div>
                            <p className="font-bold text-gray-900 text-sm md:text-base">
                              {product.product.name}
                            </p>
                            {product.product.description && (
                              <p className="text-xs text-gray-600">
                                {product.product.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <Badge className="bg-[#c9332C] text-white whitespace-nowrap text-xs md:text-sm">
                        R$ {parseFloat(product.total_value).toFixed(2)}
                      </Badge>
                    </div>

                    {product.observation && (
                      <div className="bg-yellow-50 p-2 md:p-3 rounded border border-yellow-200">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">
                          Observação:
                        </p>
                        <p className="text-xs md:text-sm text-yellow-900">
                          {product.observation}
                        </p>
                      </div>
                    )}

                    {product.selected_produtos &&
                      product.selected_produtos.length > 0 && (
                        <div className="bg-blue-50 p-2 md:p-3 rounded border border-blue-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-blue-900">
                            Produtos Selecionados
                          </p>
                          {product.selected_produtos.map((prod, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-xs md:text-sm pl-3 pb-1 border-b border-blue-100 last:border-b-0"
                            >
                              <span className="text-blue-900">
                                {prod.name}{" "}
                                <span className="text-gray-500">
                                  x{prod.qty}
                                </span>
                              </span>
                              <span className="font-semibold text-blue-900">
                                R$ {(prod.price * prod.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                    {product.selected_ingredients &&
                      product.selected_ingredients.length > 0 && (
                        <div className="bg-amber-50 p-2 md:p-3 rounded border border-amber-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-amber-900">
                            Ingredientes Inclusos
                          </p>
                          <div className="pl-3 space-y-1">
                            {product.selected_ingredients.map((ing, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between text-xs md:text-sm text-amber-900"
                              >
                                <span>• {ing.name}</span>
                                <span className="font-semibold">
                                  +R$ {(ing.price || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {product.selectedExtraIngredients &&
                      product.selectedExtraIngredients.length > 0 && (
                        <div className="bg-green-50 p-2 md:p-3 rounded border border-green-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-green-900">
                            Ingredientes Extras
                          </p>
                          <div className="pl-3 space-y-1">
                            {product.selectedExtraIngredients.map(
                              (ing, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between text-xs md:text-sm text-green-900"
                                >
                                  <span>• {ing.name}</span>
                                  <span className="font-semibold">
                                    +R$ {(ing.price || 0).toFixed(2)}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {product.selectedExtraProdutos &&
                      product.selectedExtraProdutos.length > 0 && (
                        <div className="bg-purple-50 p-2 md:p-3 rounded border border-purple-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-purple-900">
                            Produtos Extras
                          </p>
                          {product.selectedExtraProdutos.map((prod, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-xs md:text-sm pl-3 pb-1 border-b border-purple-100 last:border-b-0"
                            >
                              <span className="text-purple-900">
                                {prod.name}{" "}
                                <span className="text-gray-500">
                                  x{prod.qty}
                                </span>
                              </span>
                              <span className="font-semibold text-purple-900">
                                R$ {(prod.price * prod.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}

            {/* Total Geral */}
            <Separator />

            {/* Alerta de Taxa de Entrega */}
            {hasFreight && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-500 shadow-sm">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-orange-800 mb-2">
                      Taxa de Entrega Incluída
                    </p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">
                          Subtotal produtos:
                        </span>
                        <span className="font-semibold text-orange-900">
                          R$ {getProductsSubtotal().toFixed(2)}
                        </span>
                      </div>
                      {order.coupon_code && (
                        <div className="flex justify-between text-sm items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-700">Desconto:</span>
                            <Badge className="text-xs bg-purple-600 text-white border-0">
                              <Ticket className="h-3 w-3 mr-1" />
                              {order.coupon_code} (
                              {parseFloat(
                                order.discount_percentage || 0
                              ).toFixed(2)}
                              %)
                            </Badge>
                          </div>
                          <span className="font-semibold text-purple-700">
                            - R$ {getDiscountAmount().toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">
                          Taxa de delivery:
                        </span>
                        <span className="font-bold text-orange-900">
                          R$ {parseFloat(order.freight_value || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center bg-gradient-to-r from-green-50 to-emerald-50 p-3 md:p-4 rounded-lg border-2 border-green-300">
              <span className="text-sm md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                Total {hasFreight ? "(com taxa)" : ""}:
              </span>
              <span className="text-xl md:text-3xl font-bold text-green-600">
                R$ {calculateOrderTotal().toFixed(2)}
              </span>
            </div>

            {/* Botões de Impressão */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => onPrintOrder && onPrintOrder(order)}
                className="w-full bg-gradient-to-r from-[#c9332C] to-[#a02820] hover:from-[#a02820] hover:to-[#8a2118] text-white flex items-center justify-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button
                onClick={handleCopyText}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copiar Texto
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Texto para Copiar */}
      <Dialog open={showTextModal} onOpenChange={setShowTextModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Texto do Pedido #{order.code}
            </DialogTitle>
            <DialogDescription>
              Copie o texto abaixo e cole no software de impressão térmica
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800 font-medium mb-1">
                ℹ️ Texto otimizado para impressora térmica:
              </p>
              <p className="text-blue-700 text-xs">
                O texto copiado está <strong>sem acentos</strong> para garantir
                compatibilidade com impressoras térmicas de 58mm. Caracteres
                como "á, é, í, ó, ú, ã, õ, ç" são convertidos para "a, e, i, o,
                u, a, o, c".
              </p>
            </div>
            <textarea
              value={receiptText}
              readOnly
              className="w-full h-96 p-4 font-mono text-xs border rounded-lg bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                fontFamily: "'Courier New', monospace",
                whiteSpace: "pre",
              }}
            />
            <div className="flex gap-3">
              <Button
                onClick={copyToClipboard}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar para Área de Transferência
              </Button>
              <Button
                onClick={() => setShowTextModal(false)}
                variant="outline"
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Modal de Confirmação de Pagamento
const PaymentConfirmationModal = ({
  open,
  onOpenChange,
  order,
  onConfirm,
  loading,
}) => {
  const [paymentData, setPaymentData] = useState({
    pay_method: "",
    value_received: "",
    name: "",
    contact: "",
  });

  const paymentMethods = [
    "PIX",
    "Dinheiro",
    "Cartão de Crédito",
    "Cartão de Débito",
    "Vale Refeição",
    "Transferência Bancária",
  ];

  // Inicializar dados quando o modal abrir
  useEffect(() => {
    if (open && order) {
      const productsTotal =
        order.products?.reduce(
          (sum, product) => sum + parseFloat(product.total_value || 0),
          0
        ) || 0;

      // Adicionar taxa de entrega ao total
      const freightValue = parseFloat(order.freight_value || 0);
      const orderTotal = productsTotal + freightValue;

      setPaymentData({
        pay_method: order.pay_method || "",
        value_received: orderTotal.toFixed(2),
        name: order.client?.name || "",
        contact: order.client?.contact || "",
      });
    }
  }, [open, order]);

  const handleConfirm = () => {
    if (!paymentData.pay_method || !paymentData.value_received) {
      alert("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    onConfirm({
      ...paymentData,
      amount: parseFloat(paymentData.value_received),
      value_received: parseFloat(paymentData.value_received),
      orderCode: order.code,
    });
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Wallet className="h-5 w-5 text-[#c9332C]" />
            Confirmar Pagamento
          </DialogTitle>
          <DialogDescription className="text-sm">
            Pedido <strong>#{order.code}</strong> - {order.client?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Método de Pagamento */}
          <div className="space-y-2">
            <Label
              htmlFor="payment-method"
              className="text-sm font-semibold text-gray-700"
            >
              Método de Pagamento <span className="text-red-500">*</span>
            </Label>
            <Select
              value={paymentData.pay_method}
              onValueChange={(value) =>
                setPaymentData({ ...paymentData, pay_method: value })
              }
            >
              <SelectTrigger id="payment-method" className="h-11">
                <SelectValue placeholder="Selecione o método de pagamento" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method} value={method}>
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Valor Recebido */}
          <div className="space-y-2">
            <Label
              htmlFor="value-received"
              className="text-sm font-semibold text-gray-700"
            >
              Valor Recebido (R$) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="value-received"
              type="number"
              step="0.01"
              min="0"
              value={paymentData.value_received}
              onChange={(e) =>
                setPaymentData({
                  ...paymentData,
                  value_received: e.target.value,
                })
              }
              className="h-11"
              placeholder="0.00"
            />
          </div>

          {/* Nome do Cliente */}
          <div className="space-y-2">
            <Label
              htmlFor="client-name"
              className="text-sm font-semibold text-gray-700"
            >
              Nome do Cliente
            </Label>
            <Input
              id="client-name"
              type="text"
              value={paymentData.name}
              onChange={(e) =>
                setPaymentData({ ...paymentData, name: e.target.value })
              }
              className="h-11"
              placeholder="Nome do cliente"
            />
          </div>

          {/* Contato */}
          <div className="space-y-2">
            <Label
              htmlFor="contact"
              className="text-sm font-semibold text-gray-700"
            >
              Contato
            </Label>
            <Input
              id="contact"
              type="text"
              value={paymentData.contact}
              onChange={(e) =>
                setPaymentData({ ...paymentData, contact: e.target.value })
              }
              className="h-11"
              placeholder="Telefone ou e-mail"
            />
          </div>

          {/* Resumo */}
          <div className="bg-gray-50 p-4 rounded-lg border space-y-2">
            <p className="text-xs font-semibold text-gray-600 uppercase">
              Resumo
            </p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pedido:</span>
                <span className="font-semibold">#{order.code}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pagamento:</span>
                <span className="font-semibold">
                  {paymentData.pay_method || "-"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-bold text-[#c9332C]">
                  R$ {paymentData.value_received || "0.00"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              loading || !paymentData.pay_method || !paymentData.value_received
            }
            className="flex-1 bg-[#c9332C] hover:bg-[#a02820]"
          >
            {loading ? "Confirmando..." : "Confirmar Pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Card de Pedido Responsivo
const OrderCard = ({
  order,
  onViewDetails,
  onUpdateStatus,
  onWhatsApp,
  onPrint,
  onConfirmPayment,
}) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      Fechado: "bg-red-100 text-red-800 border-red-200",
      Recebido: "bg-blue-100 text-blue-800 border-blue-200",
      Cancelado: "bg-gray-100 text-gray-800 border-gray-200",
      Finalizado: "bg-green-100 text-green-800 border-green-200",
      Preparando: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Liberado: "bg-purple-100 text-purple-800 border-purple-200",
      Entregue: "bg-teal-100 text-teal-800 border-teal-200",
    };
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const calculateTotal = () => {
    const productsTotal =
      order.products?.reduce(
        (sum, product) => sum + parseFloat(product.total_value || 0),
        0
      ) || 0;

    // Calcular desconto do cupom (aplicado apenas nos produtos)
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    const discountAmount = productsTotal * (discountPercentage / 100);
    const subtotalWithDiscount = productsTotal - discountAmount;

    // Adicionar taxa de entrega se existir
    const freightValue = parseFloat(order.freight_value || 0);
    return subtotalWithDiscount + freightValue;
  };

  const hasFreight =
    order.delivery_type?.toLowerCase() === "delivery" &&
    parseFloat(order.freight_value || 0) > 0;

  // Determinar cor da borda: verde se finalizado, vermelho caso contrário
  const borderColor = order.payed === true ? "#22c55e" : "#c9332C";

  return (
    <Card
      className="overflow-hidden hover:shadow-lg transition-all duration-200 border-l-4"
      style={{ borderLeftColor: borderColor }}
    >
      <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex justify-between items-start gap-2">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className="text-sm md:text-base font-bold border-[#c9332C] text-[#c9332C]"
              >
                #{order.code}
              </Badge>
              <Badge
                className={`text-xs md:text-sm border ${getStatusColor(
                  order.status
                )}`}
              >
                {order.status}
              </Badge>
              {order.payed === true && (
                <Badge className="text-xs md:text-sm bg-green-600 text-white border-0 shadow-sm">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Pago
                </Badge>
              )}
              {hasFreight && (
                <Badge className="text-xs md:text-sm bg-orange-500 text-white border-0 shadow-sm animate-pulse">
                  <DollarSign className="h-3 w-3 mr-1" />+ Taxa
                </Badge>
              )}
              {order.coupon_code && (
                <Badge className="text-xs md:text-sm bg-purple-600 text-white border-0 shadow-sm">
                  <Ticket className="h-3 w-3 mr-1" />
                  {order.coupon_code}
                </Badge>
              )}
            </div>
            <p className="text-sm md:text-base font-semibold text-gray-900 truncate">
              {order.client?.name || "Cliente não informado"}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
              {order.date_time && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{order.date_time}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <ShoppingBag className="h-3 w-3" />
                <span>{order.products?.length || 0} itens</span>
              </div>
            </div>
          </div>
          <div className="text-right space-y-1">
            <div className="text-lg md:text-xl font-bold text-[#c9332C]">
              R$ {calculateTotal().toFixed(2)}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-3 pb-3 space-y-3">
        {/* Ações Rápidas - Sempre Visíveis */}
        <div className="flex flex-col gap-2">
          {/* Linha 1: Ações principais */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs md:text-sm text-blue-600 hover:bg-blue-50 border-blue-300"
              onClick={() => onViewDetails(order)}
            >
              <Eye className="h-3 w-3 md:h-4 md:w-4 mr-1" />
              Detalhes
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs md:text-sm text-green-600 hover:bg-green-50 border-green-300"
              onClick={() => onWhatsApp(order)}
            >
              <MessageCircleCode className="h-3 w-3 md:h-4 md:w-4 mr-1" />
              WhatsApp
            </Button>
          </div>

          {/* Botão de Expandir Ações */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-gray-600 hover:bg-gray-100"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Menos ações
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Mais ações
              </>
            )}
          </Button>
        </div>

        {/* Conteúdo Expandido */}
        {expanded && (
          <div className="space-y-3 pt-3 border-t">
            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                <p className="text-xs text-blue-600 font-semibold">Contato</p>
                <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                  {order.client?.contact || "N/A"}
                </p>
              </div>
              <div className="bg-green-50 p-2 rounded border border-green-200">
                <p className="text-xs text-green-600 font-semibold">
                  Pagamento
                </p>
                <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                  {order.pay_method || "N/A"}
                </p>
              </div>
              <div className="bg-purple-50 p-2 rounded border border-purple-200">
                <p className="text-xs text-purple-600 font-semibold">Entrega</p>
                <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                  {order.delivery_type || "N/A"}
                </p>
              </div>
              <div className="bg-amber-50 p-2 rounded border border-amber-200">
                <p className="text-xs text-amber-600 font-semibold">Total</p>
                <p className="text-xs md:text-sm font-bold text-gray-900">
                  R$ {calculateTotal().toFixed(2)}
                </p>
              </div>
            </div>

            {/* Alerta de Taxa de Entrega */}
            {hasFreight && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border-l-4 border-orange-500 shadow-sm">
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-orange-800 mb-1">
                      Taxa de Entrega Incluída
                    </p>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-xs text-orange-700">
                        Subtotal produtos:{" "}
                        <span className="font-semibold">
                          R${" "}
                          {(
                            calculateTotal() -
                            parseFloat(order.freight_value || 0)
                          ).toFixed(2)}
                        </span>
                      </p>
                      <p className="text-xs text-orange-700">
                        Taxa delivery:{" "}
                        <span className="font-bold">
                          R$ {parseFloat(order.freight_value || 0).toFixed(2)}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Observações (se houver) */}
            {order.products?.some((p) => p.observation) && (
              <div className="bg-blue-50 p-2 rounded-lg border-l-4 border-blue-500">
                <p className="text-xs text-blue-600 font-semibold mb-1">
                  Observações:
                </p>
                <div className="space-y-1">
                  {order.products
                    ?.filter((p) => p.observation)
                    .map((p, idx) => (
                      <p key={idx} className="text-xs text-blue-900">
                        • {p.observation}
                      </p>
                    ))}
                </div>
              </div>
            )}

            {/* Ações de Gerenciamento */}
            <div className="space-y-2 pt-2 border-t">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-blue-600 hover:bg-blue-50 h-12 md:h-14 font-semibold"
                  onClick={() => onPrint(order)}
                >
                  <Printer className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Imprimir
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-emerald-600 hover:bg-emerald-50 h-12 md:h-14 font-semibold border-emerald-300"
                  onClick={() => onConfirmPayment(order)}
                >
                  <Wallet className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Pagamento
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-blue-600 hover:bg-blue-50 h-12 md:h-14 font-semibold"
                  onClick={() => onUpdateStatus(order.code, "Liberado")}
                >
                  <Bell className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Liberar
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-yellow-600 hover:bg-yellow-50 h-12 md:h-14 font-semibold"
                  onClick={() => onUpdateStatus(order.code, "Preparando")}
                >
                  <CircleDollarSign className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Preparando
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-purple-600 hover:bg-purple-50 h-12 md:h-14 font-semibold"
                  onClick={() => onUpdateStatus(order.code, "Entregue")}
                >
                  <PackageCheck className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Entregue
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-green-600 hover:bg-green-50 h-12 md:h-14 font-semibold"
                  onClick={() => onUpdateStatus(order.code, "Finalizado")}
                >
                  <Check className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Finalizar
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-red-600 hover:bg-red-50 h-12 md:h-14 font-semibold"
                  onClick={() => onUpdateStatus(order.code, "Cancelado")}
                >
                  <Angry className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Componente Principal
const OrdersManagement = ({ initialStatus = "" }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [status, setStatus] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("all");
  const [selectedDeliveryType, setSelectedDeliveryType] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchCode, setSearchCode] = useState("");
  const [searchClient, setSearchClient] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [printSuccessOpen, setPrintSuccessOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [statusUpdateInfo, setStatusUpdateInfo] = useState({
    orderCode: "",
    status: "",
    success: true,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false);

  useEffect(() => {
    fetchOrders(initialStatus);
    fetchStatus();
  }, []);

  useEffect(() => {
    let filtered = orders;

    if (searchCode.trim()) {
      const normalizedSearch = searchCode.trim().toLowerCase();
      filtered = filtered.filter((order) =>
        String(order?.code || "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    }

    if (searchClient.trim()) {
      const normalizedClientSearch = searchClient.trim().toLowerCase();
      filtered = filtered.filter((order) => {
        const clientName = order.client?.name || order.client_name || "";
        return clientName.toLowerCase().includes(normalizedClientSearch);
      });
    }

    // Filtro por status de pagamento
    if (selectedPaymentStatus === "paid") {
      filtered = filtered.filter((order) => order.payed === true);
    } else if (selectedPaymentStatus === "unpaid") {
      filtered = filtered.filter((order) => order.payed !== true);
    }

    // Filtro por tipo de entrega
    if (selectedDeliveryType === "delivery") {
      filtered = filtered.filter(
        (order) => order.delivery_type?.toLowerCase() === "delivery"
      );
    } else if (selectedDeliveryType === "pickup") {
      filtered = filtered.filter(
        (order) =>
          order.delivery_type?.toLowerCase() === "retirar" ||
          order.delivery_type?.toLowerCase() === "retirada" ||
          order.delivery_type?.toLowerCase() === "pickup"
      );
    }

    if (startDate || endDate) {
      const createBoundaryDate = (value, isEndOfDay) => {
        if (!value) return null;
        const [year, month, day] = value
          .split("-")
          .map((segment) => Number.parseInt(segment, 10));
        if ([year, month, day].some((part) => Number.isNaN(part))) return null;
        return new Date(
          year,
          month - 1,
          day,
          isEndOfDay ? 23 : 0,
          isEndOfDay ? 59 : 0,
          isEndOfDay ? 59 : 0,
          isEndOfDay ? 999 : 0
        );
      };

      const startBoundary = createBoundaryDate(startDate, false);
      const endBoundary = createBoundaryDate(endDate, true);

      filtered = filtered.filter((order) => {
        const orderDate = parseOrderDate(order);

        // Se não há data válida no pedido, excluir do resultado
        if (!orderDate) return false;
        // Se há data inicial e a data do pedido é anterior, excluir
        if (startBoundary && orderDate < startBoundary) return false;
        // Se há data final e a data do pedido é posterior, excluir
        if (endBoundary && orderDate > endBoundary) return false;
        return true;
      });
    }

    filtered.sort((a, b) => {
      const dateA = parseOrderDate(a);
      const dateB = parseOrderDate(b);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return sortOrder === "desc"
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    });

    setFilteredOrders(filtered);
  }, [
    orders,
    searchCode,
    searchClient,
    startDate,
    endDate,
    sortOrder,
    selectedPaymentStatus,
    selectedDeliveryType,
  ]);

  useEffect(() => {
    const newTotalPages = Math.max(
      1,
      Math.ceil((filteredOrders.length || 0) / PAGE_SIZE)
    );
    setCurrentPage((prev) => {
      const normalized = Math.min(Math.max(prev, 1), newTotalPages);
      return normalized !== prev ? normalized : prev;
    });
  }, [filteredOrders]);

  const fetchOrders = async (param = "") => {
    setLoading(true);
    setLoadingMessage("Carregando pedidos...");
    try {
      const params = new URLSearchParams();
      if (param) params.append("status", param);
      params.append("sort", "desc");
      const response = await axios.get(
        `${API}/orders/admin?${params.toString()}`
      );
      const normalized = Array.isArray(response.data.data)
        ? response.data.data.map(attachNormalizedAddresses)
        : [];
      setOrders(normalized);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/status`);
      setStatus(response.data.data);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const handleUpdateStatus = async (codigoPedido, novoStatus) => {
    setLoading(true);
    setLoadingMessage(
      `Atualizando pedido #${codigoPedido} para ${novoStatus}...`
    );
    try {
      await axios.put(
        `${API}/orders/${codigoPedido}/status`,
        { status: novoStatus },
        { headers: { "Content-Type": "application/json" } }
      );
      await fetchOrders(selectedStatus);
      setStatusUpdateInfo({
        orderCode: codigoPedido,
        status: novoStatus,
        success: true,
      });
      setStatusUpdateModalOpen(true);
    } catch (error) {
      console.error("Error updating order status:", error);
      setStatusUpdateInfo({
        orderCode: codigoPedido,
        status: novoStatus,
        success: false,
      });
      setStatusUpdateModalOpen(true);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleFetchOrdersByStatus = async (statusKey) => {
    setSelectedStatus(statusKey);
    setLoading(true);
    setLoadingMessage(
      statusKey
        ? `Filtrando pedidos por ${statusKey}...`
        : "Carregando todos os pedidos..."
    );
    try {
      const params = new URLSearchParams();
      if (statusKey !== "") params.append("status", statusKey);
      params.append("sort", "desc");
      const response = await axios.get(
        `${API}/orders/admin?${params.toString()}`
      );
      const normalized = Array.isArray(response.data.data)
        ? response.data.data.map(attachNormalizedAddresses)
        : [];
      setOrders(normalized);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const handleSendWhatsApp = (order) => {
    try {
      const rawPhone =
        order?.client?.contact ||
        order?.client?.phone ||
        order?.customer_phone ||
        "";
      const digits = rawPhone.toString().replace(/\D/g, "");

      if (!digits) {
        alert("Contato do cliente não disponível para WhatsApp.");
        return;
      }

      const phoneWithCountry = digits.startsWith("55") ? digits : `55${digits}`;
      const customerName = order?.client?.name || "cliente";
      const total =
        order.products?.reduce(
          (sum, product) => sum + Number(product.total_value || 0),
          0
        ) || 0;

      const lines = [
        `Olá ${customerName}! Aqui é a equipe Bonfim Gourmet 🍧`,
        `Estamos entrando em contato sobre o pedido #${order.code || ""}${
          order.status ? ` (${order.status})` : ""
        }.`,
        total ? `Valor do pedido: R$ ${total.toFixed(2)}` : "",
        order?.delivery_type ? `Tipo de entrega: ${order.delivery_type}` : "",
        "Qualquer dúvida é só responder! 🙌",
      ].filter(Boolean);

      const message = encodeURIComponent(lines.join("\n"));
      const whatsappUrl = `https://wa.me/${phoneWithCountry}?text=${message}`;

      const opened = window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      if (!opened) window.location.href = whatsappUrl;
    } catch (error) {
      console.error("Erro ao abrir WhatsApp:", error);
      alert("Não foi possível abrir o WhatsApp.");
    }
  };

  const handleConfirmPayment = (order) => {
    setSelectedOrderForPayment(order);
    setPaymentModalOpen(true);
  };

  const handlePaymentConfirm = async (paymentData) => {
    setLoading(true);
    setLoadingMessage(
      `Confirmando pagamento do pedido #${paymentData.orderCode}...`
    );
    try {
      await pixService.confirmPayment(paymentData.orderCode, paymentData);

      // Atualizar status do pedido para Finalizado
      //await handleUpdateStatus(paymentData.orderCode, "Finalizado");

      setPaymentModalOpen(false);
      setPaymentSuccessOpen(true);

      // Recarregar pedidos
      await fetchOrders(selectedStatus);
    } catch (error) {
      console.error("Erro ao confirmar pagamento:", error);
      alert("Erro ao confirmar pagamento. Tente novamente.");
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const totalOrders = filteredOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalOrders / PAGE_SIZE));
  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(
    pageStartIndex,
    pageStartIndex + PAGE_SIZE
  );

  // Calcular totais de valores pagos e pendentes
  const calculateOrderTotal = (order) => {
    if (!order.products || !Array.isArray(order.products)) return 0;
    return order.products.reduce((sum, product) => {
      return sum + parseFloat(product.total_value || 0);
    }, 0);
  };

  const totalPaid = filteredOrders
    .filter((order) => order.payed === true)
    .reduce((sum, order) => sum + calculateOrderTotal(order), 0);

  const totalUnpaid = filteredOrders
    .filter((order) => order.payed !== true)
    .reduce((sum, order) => sum + calculateOrderTotal(order), 0);

  const paidCount = filteredOrders.filter(
    (order) => order.payed === true
  ).length;
  const unpaidCount = filteredOrders.filter(
    (order) => order.payed !== true
  ).length;

  // Calcular contagens por tipo de entrega
  const deliveryCount = filteredOrders.filter(
    (order) => order.delivery_type?.toLowerCase() === "delivery"
  ).length;
  const pickupCount = filteredOrders.filter(
    (order) =>
      order.delivery_type?.toLowerCase() === "retirar" ||
      order.delivery_type?.toLowerCase() === "retirada" ||
      order.delivery_type?.toLowerCase() === "pickup"
  ).length;

  // Calcular totais de frete
  const ordersWithFreight = filteredOrders.filter(
    (order) =>
      order.delivery_type?.toLowerCase() === "delivery" &&
      parseFloat(order.freight_value || 0) > 0
  );

  const totalFreightCount = ordersWithFreight.length;

  const totalFreightValue = ordersWithFreight.reduce((sum, order) => {
    return sum + parseFloat(order.freight_value || 0);
  }, 0);

  const averageFreight =
    totalFreightCount > 0 ? totalFreightValue / totalFreightCount : 0;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Gestão de Pedidos
          </h2>
          <Badge variant="outline" className="text-base md:text-lg px-3 py-1">
            Total:{" "}
            <span className="font-bold ml-1">{filteredOrders.length}</span>
          </Badge>
        </div>

        {/* Cards de Totais */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-600">Pagos</p>
                  <p className="text-lg md:text-xl font-bold text-green-900">
                    R$ {totalPaid.toFixed(2)}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {paidCount} pedidos
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 md:h-10 md:w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-orange-600">
                    Pendentes
                  </p>
                  <p className="text-lg md:text-xl font-bold text-orange-900">
                    R$ {totalUnpaid.toFixed(2)}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    {unpaidCount} pedidos
                  </p>
                </div>
                <Clock className="h-8 w-8 md:h-10 md:w-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-600">
                    Total Geral
                  </p>
                  <p className="text-lg md:text-xl font-bold text-blue-900">
                    R$ {(totalPaid + totalUnpaid).toFixed(2)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {filteredOrders.length} pedidos
                  </p>
                </div>
                <DollarSign className="h-8 w-8 md:h-10 md:w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-purple-600">
                    Ticket Médio
                  </p>
                  <p className="text-lg md:text-xl font-bold text-purple-900">
                    R${" "}
                    {filteredOrders.length > 0
                      ? (
                          (totalPaid + totalUnpaid) /
                          filteredOrders.length
                        ).toFixed(2)
                      : "0.00"}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">por pedido</p>
                </div>
                <Wallet className="h-8 w-8 md:h-10 md:w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-amber-600">
                    Com Frete
                  </p>
                  <p className="text-lg md:text-xl font-bold text-amber-900">
                    {totalFreightCount}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    pedidos delivery
                  </p>
                </div>
                <Truck className="h-8 w-8 md:h-10 md:w-10 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-teal-600">
                    Frete Médio
                  </p>
                  <p className="text-lg md:text-xl font-bold text-teal-900">
                    R$ {averageFreight.toFixed(2)}
                  </p>
                  <p className="text-xs text-teal-600 mt-1">por delivery</p>
                </div>
                <Truck className="h-8 w-8 md:h-10 md:w-10 text-teal-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filtros e Busca */}
      <Card>
        <CardContent className="p-3 md:p-4 space-y-3">
          {/* Busca */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por código..."
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                className="pl-9 pr-9 h-10 text-sm"
              />
              {searchCode && (
                <button
                  onClick={() => setSearchCode("")}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por cliente..."
                value={searchClient}
                onChange={(e) => setSearchClient(e.target.value)}
                className="pl-9 pr-9 h-10 text-sm"
              />
              {searchClient && (
                <button
                  onClick={() => setSearchClient("")}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Botão Mostrar/Ocultar Filtros */}
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto h-12 md:h-10 text-sm font-medium hover:bg-gray-50"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters
              ? "Ocultar Filtros Avançados"
              : "Mostrar Filtros Avançados"}
            {showFilters ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>

          {/* Filtros Avançados (Colapsáveis em Mobile) */}
          {showFilters && (
            <div className="space-y-3 pt-3 border-t">
              {/* Filtro de Data */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Data inicial
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || undefined}
                      className="pl-9 h-10 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Data final
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || undefined}
                      className="pl-9 h-10 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Ordenação */}
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="h-10 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Mais recentes</SelectItem>
                    <SelectItem value="asc">Mais antigos</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="lg"
                  disabled={!startDate && !endDate}
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="h-10 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar datas
                </Button>
              </div>
            </div>
          )}

          {/* Filtros de Pagamento */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-600">
              Filtrar por Pagamento
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={
                  selectedPaymentStatus === "all" ? "default" : "outline"
                }
                size="lg"
                className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                  selectedPaymentStatus === "all"
                    ? "bg-[#c9332C] hover:bg-[#a02820] text-white shadow-md"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedPaymentStatus("all")}
              >
                <Wallet className="h-4 w-4 mr-2" />
                Todos
                <Badge
                  variant="secondary"
                  className={`ml-2 ${
                    selectedPaymentStatus === "all"
                      ? "bg-white/20 text-white border-0"
                      : "bg-gray-100"
                  }`}
                >
                  {filteredOrders.length}
                </Badge>
              </Button>

              <Button
                variant={
                  selectedPaymentStatus === "paid" ? "default" : "outline"
                }
                size="lg"
                className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                  selectedPaymentStatus === "paid"
                    ? "bg-green-600 hover:bg-green-700 text-white shadow-md"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedPaymentStatus("paid")}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Pagos
                <Badge
                  variant="secondary"
                  className={`ml-2 ${
                    selectedPaymentStatus === "paid"
                      ? "bg-white/20 text-white border-0"
                      : "bg-gray-100"
                  }`}
                >
                  {paidCount}
                </Badge>
              </Button>

              <Button
                variant={
                  selectedPaymentStatus === "unpaid" ? "default" : "outline"
                }
                size="lg"
                className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                  selectedPaymentStatus === "unpaid"
                    ? "bg-orange-600 hover:bg-orange-700 text-white shadow-md"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedPaymentStatus("unpaid")}
              >
                <Clock className="h-4 w-4 mr-2" />
                Pendentes
                <Badge
                  variant="secondary"
                  className={`ml-2 ${
                    selectedPaymentStatus === "unpaid"
                      ? "bg-white/20 text-white border-0"
                      : "bg-gray-100"
                  }`}
                >
                  {unpaidCount}
                </Badge>
              </Button>
            </div>
          </div>

          {/* Filtros de Tipo de Entrega */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-600">
              Filtrar por Tipo de Entrega
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedDeliveryType === "all" ? "default" : "outline"}
                size="lg"
                className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                  selectedDeliveryType === "all"
                    ? "bg-[#c9332C] hover:bg-[#a02820] text-white shadow-md"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedDeliveryType("all")}
              >
                <Package className="h-4 w-4 mr-2" />
                Todos
                <Badge
                  variant="secondary"
                  className={`ml-2 ${
                    selectedDeliveryType === "all"
                      ? "bg-white/20 text-white border-0"
                      : "bg-gray-100"
                  }`}
                >
                  {filteredOrders.length}
                </Badge>
              </Button>

              <Button
                variant={
                  selectedDeliveryType === "delivery" ? "default" : "outline"
                }
                size="lg"
                className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                  selectedDeliveryType === "delivery"
                    ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedDeliveryType("delivery")}
              >
                <Truck className="h-4 w-4 mr-2" />
                Delivery
                <Badge
                  variant="secondary"
                  className={`ml-2 ${
                    selectedDeliveryType === "delivery"
                      ? "bg-white/20 text-white border-0"
                      : "bg-gray-100"
                  }`}
                >
                  {deliveryCount}
                </Badge>
              </Button>

              <Button
                variant={
                  selectedDeliveryType === "pickup" ? "default" : "outline"
                }
                size="lg"
                className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                  selectedDeliveryType === "pickup"
                    ? "bg-purple-600 hover:bg-purple-700 text-white shadow-md"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedDeliveryType("pickup")}
              >
                <ShoppingBag className="h-4 w-4 mr-2" />
                Retirada
                <Badge
                  variant="secondary"
                  className={`ml-2 ${
                    selectedDeliveryType === "pickup"
                      ? "bg-white/20 text-white border-0"
                      : "bg-gray-100"
                  }`}
                >
                  {pickupCount}
                </Badge>
              </Button>
            </div>
          </div>

          {/* Filtros de Status */}
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-600">
              Filtrar por Status
            </label>
            <div className="overflow-x-auto pb-2 -mx-3 px-3 md:mx-0 md:px-0">
              <div className="flex gap-2 min-w-max md:min-w-0 md:flex-wrap">
                <Button
                  variant={selectedStatus === "" ? "default" : "outline"}
                  size="lg"
                  className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                    selectedStatus === ""
                      ? "bg-[#c9332C] hover:bg-[#a02820] text-white shadow-md"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleFetchOrdersByStatus("")}
                >
                  <Package className="h-4 w-4 mr-2" />
                  Todos
                  <Badge
                    variant="secondary"
                    className={`ml-2 ${
                      selectedStatus === ""
                        ? "bg-white/20 text-white border-0"
                        : "bg-gray-100"
                    }`}
                  >
                    {filteredOrders.length}
                  </Badge>
                </Button>

                {status.map((s) => {
                  const count = filteredOrders.filter(
                    (o) => o.status === s.key
                  ).length;
                  return (
                    <Button
                      key={s.key}
                      variant={selectedStatus === s.key ? "default" : "outline"}
                      size="lg"
                      className={`h-12 md:h-10 px-4 text-sm font-medium whitespace-nowrap ${
                        selectedStatus === s.key
                          ? "bg-[#c9332C] hover:bg-[#a02820] text-white shadow-md"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() => handleFetchOrdersByStatus(s.key)}
                    >
                      {s.key}
                      <Badge
                        variant="secondary"
                        className={`ml-2 ${
                          selectedStatus === s.key
                            ? "bg-white/20 text-white border-0"
                            : "bg-gray-100"
                        }`}
                      >
                        {count}
                      </Badge>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Pedidos */}
      <div className="space-y-3 md:space-y-4">
        {paginatedOrders.length > 0 ? (
          paginatedOrders.map((order) => (
            <OrderCard
              key={order.code}
              order={order}
              onViewDetails={handleViewDetails}
              onUpdateStatus={handleUpdateStatus}
              onWhatsApp={handleSendWhatsApp}
              onPrint={printOrder}
              onConfirmPayment={handleConfirmPayment}
            />
          ))
        ) : (
          <Card>
            <CardContent className="p-8 md:p-12 text-center">
              <ShoppingBag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-base md:text-lg">
                Nenhum pedido encontrado
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="text-xs md:text-sm text-gray-600 text-center sm:text-left">
                Mostrando{" "}
                <span className="font-semibold">
                  {pageStartIndex + 1}-
                  {Math.min(pageStartIndex + PAGE_SIZE, totalOrders)}
                </span>{" "}
                de <span className="font-semibold">{totalOrders}</span> pedidos
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className="text-xs"
                >
                  Anterior
                </Button>

                <div className="flex items-center gap-1">
                  <span className="text-xs md:text-sm text-gray-600">
                    Pág. {currentPage} de {totalPages}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="text-xs"
                >
                  Próximo
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modais */}
      <OrderDetailsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        order={selectedOrder}
        onPrintOrder={printOrder}
      />

      {/* Modal de Status Atualizado */}
      <Dialog
        open={statusUpdateModalOpen}
        onOpenChange={setStatusUpdateModalOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statusUpdateInfo.success ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                  <span className="text-green-900">Status Atualizado</span>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <span className="text-red-900">Erro na Atualização</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {statusUpdateInfo.success ? (
              <div className="space-y-3">
                <p className="text-gray-700 text-center">
                  O status do pedido foi atualizado com sucesso!
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 font-medium">
                      Pedido:
                    </span>
                    <Badge
                      variant="outline"
                      className="text-base font-bold border-[#c9332C] text-[#c9332C]"
                    >
                      #{statusUpdateInfo.orderCode}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 font-medium">
                      Novo Status:
                    </span>
                    <Badge className="bg-green-600 text-white text-sm font-semibold">
                      {statusUpdateInfo.status}
                    </Badge>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-gray-700 text-center">
                  Não foi possível atualizar o status do pedido.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 text-center">
                    Pedido <strong>#{statusUpdateInfo.orderCode}</strong>
                  </p>
                  <p className="text-xs text-red-600 text-center mt-2">
                    Por favor, tente novamente ou entre em contato com o
                    suporte.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setStatusUpdateModalOpen(false)}
              className="bg-[#c9332C] hover:bg-[#a02820] text-white"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={printSuccessOpen} onOpenChange={setPrintSuccessOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Impressão enviada</DialogTitle>
            <DialogDescription className="mt-2">
              A impressão do pedido foi enviada com sucesso!
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-end">
            <Button onClick={() => setPrintSuccessOpen(false)}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Pagamento */}
      <PaymentConfirmationModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        order={selectedOrderForPayment}
        onConfirm={handlePaymentConfirm}
        loading={loading}
      />

      {/* Modal de Pagamento Confirmado */}
      <Dialog open={paymentSuccessOpen} onOpenChange={setPaymentSuccessOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-green-600">
              <CheckCircle className="h-6 w-6" />
              Pagamento Confirmado!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-3">
              <p className="text-gray-700 text-center">
                O pagamento foi confirmado e o pedido foi finalizado com
                sucesso!
              </p>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 text-center mt-2">
                  O status do pedido foi atualizado para{" "}
                  <strong>Finalizado</strong>
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setPaymentSuccessOpen(false)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Loading Global */}
      <Dialog open={loading} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Carregando</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              {/* Spinner animado */}
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              {/* Ícone central */}
              <div className="absolute inset-0 flex items-center justify-center">
                <ShoppingBag className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              {loadingMessage || "Processando..."}
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManagement;
